import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';
import { SparePlayer } from '../../types/matchReport.ts';

interface PlayerSearchAutocompleteProps {
  onPlayerSelected: (_player: SparePlayer) => void;
  label?: string;
  placeholder?: string;
}

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 1000;

const PlayerSearchAutocomplete: React.FC<PlayerSearchAutocompleteProps> = ({
  onPlayerSelected,
  label = 'Search players',
  placeholder = 'Type player name...',
}) => {
  const [options, setOptions] = useState<SparePlayer[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const performSearch = useCallback(
    async (query: string) => {
      if (query.length < MIN_QUERY_LENGTH) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const players = await unifiedApi.get(`/api/UserTenant/search?q=${encodeURIComponent(query)}`);
        const mapped: SparePlayer[] = Array.isArray(players)
          ? players.map((p: any) => ({
              userTenantId: p.id,
              firstName: p.firstName,
              lastName: p.lastName,
              email: p.email,
              name: `${p.firstName} ${p.lastName}`,
              teamName: p.teamRole || 'Player',
            }))
          : [];
        
        // Deduplicate by email address, keeping first occurrence
        const seen = new Set<string>();
        const deduplicated = mapped.filter(player => {
          if (!player.email || seen.has(player.email.toLowerCase())) {
            return false;
          }
          seen.add(player.email.toLowerCase());
          return true;
        });
        
        setOptions(deduplicated);
      } catch (err) {
        console.warn('Failed to search players:', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!inputValue || inputValue.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      return;
    }

    const handle = window.setTimeout(() => {
      performSearch(inputValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [inputValue, performSearch]);

  return (
    <Autocomplete<SparePlayer>
      options={options}
      getOptionLabel={(option) => option.name || `${option.firstName} ${option.lastName}`}
      filterOptions={(x) => x}
      value={null}
      inputValue={inputValue}
      loading={loading}
      onInputChange={(_, newValue) => {
        setInputValue(newValue);
      }}
      onChange={(_, newValue) => {
        if (newValue) {
          onPlayerSelected(newValue);
          setInputValue('');
          setOptions([]);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.userTenantId}>
          <Typography>
            {option.name || `${option.firstName} ${option.lastName}`}
            {option.email && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({option.email})
              </Typography>
            )}
          </Typography>
        </Box>
      )}
      noOptionsText={
        !inputValue || inputValue.length < MIN_QUERY_LENGTH
          ? 'Type at least 2 characters'
          : 'No players found'
      }
    />
  );
};

export default PlayerSearchAutocomplete;
